import { useTranslation } from "react-i18next";
import { Badge, Button, Input } from "../ui";
import { TOLLS_SOURCE_TONES } from "../../features/quotes/statusTones";
import { sumTolls } from "../../features/quotes/calc";
import type { QuoteTolls, TollItem } from "../../features/quotes/types";
import { formatMoney } from "./QuoteTotals";

interface TollsEditorProps {
  tolls: QuoteTolls;
  /** Casetas capturadas a mano (null = se usan las del servidor). */
  manualItems: TollItem[] | null;
  onChange: (items: TollItem[] | null) => void;
  onRefresh?: () => void;
  refreshing?: boolean;
  error?: string | null;
  currency: string;
  readOnly?: boolean;
}

/** Tabla editable de casetas. Editar cualquier ítem vuelve la fuente "manual". */
export function TollsEditor({ tolls, manualItems, onChange, onRefresh, refreshing, error, currency, readOnly }: TollsEditorProps) {
  const { t } = useTranslation("finanzasOperativas");
  const items = manualItems ?? tolls.items;
  const source = manualItems ? "manual" : tolls.source;
  const total = manualItems ? sumTolls(manualItems) : tolls.total;

  const startManual = (): TollItem[] => (manualItems ? [...manualItems] : tolls.items.map((it) => ({ name: it.name, price: it.price })));

  const update = (index: number, patch: Partial<TollItem>) => {
    const next = startManual();
    next[index] = { ...next[index], ...patch };
    onChange(next);
  };
  const remove = (index: number) => {
    const next = startManual();
    next.splice(index, 1);
    onChange(next);
  };
  const add = () => onChange([...startManual(), { name: "", price: 0 }]);

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <Badge tone={TOLLS_SOURCE_TONES[source] ?? "neutral"} variant="outline">
          {t(`quotes.calculation.tollsSource.${source}`, { defaultValue: source })}
          {tolls.cached && !manualItems ? ` · ${t("quotes.calculation.tollsCached")}` : ""}
        </Badge>
        <span className="text-sm font-semibold tabular-nums text-content">{formatMoney(total, currency)}</span>
        <span className="flex-1" />
        {!readOnly && manualItems && (
          <Button variant="ghost" size="sm" onClick={() => onChange(null)}>
            {t("quotes.calculation.restore")}
          </Button>
        )}
        {!readOnly && onRefresh && (
          <Button variant="outline" size="sm" loading={refreshing} onClick={onRefresh} icon={<i className="ri-refresh-line" />}>
            {t("quotes.calculation.refreshTolls")}
          </Button>
        )}
      </div>

      {error && <p className="text-xs text-warning">{t("quotes.calculation.tollsError")}</p>}
      {!error && !manualItems && tolls.source === "google" && (
        <p className="text-xs text-content-faint">
          {tolls.coverage === false
            ? t("quotes.calculation.noCoverage")
            : t("quotes.calculation.tollsAxleHint", { factor: tolls.axle_factor })}
        </p>
      )}

      {items.length > 0 && (
        <div className="space-y-1.5">
          {items.map((it, i) => (
            <div key={`${i}-${it.leg_index ?? ""}`} className="flex items-center gap-2">
              <Input
                size="sm"
                containerClassName="flex-1"
                aria-label={t("quotes.calculation.tollName")}
                placeholder={t("quotes.calculation.tollName")}
                value={it.name}
                readOnly={readOnly}
                onChange={(e) => update(i, { name: e.target.value })}
              />
              <Input
                size="sm"
                type="number"
                min={0}
                step="0.01"
                containerClassName="w-32"
                className="text-right tabular-nums"
                aria-label={t("quotes.calculation.tollPrice")}
                value={it.price}
                readOnly={readOnly}
                onChange={(e) => update(i, { price: Number(e.target.value) })}
              />
              {!readOnly && (
                <Button variant="ghost" size="sm" iconOnly className="shrink-0" title={t("quotes.calculation.remove")} icon={<i className="ri-close-line" />} onClick={() => remove(i)} />
              )}
            </div>
          ))}
        </div>
      )}

      {!readOnly && (
        <Button variant="dashed" size="sm" fullWidth icon={<i className="ri-add-line" />} onClick={add}>
          {t("quotes.calculation.addToll")}
        </Button>
      )}
    </div>
  );
}
